import React, {useState, useRef, useEffect} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../../hooks/useAuth';
import {useAppSelector} from '../../hooks/redux';
import {selectCurrentPatient} from '../../slices/patientSlice';
import {isUserAdmin} from '../../utils/tokenUtils';
import {ThemedLogo} from '../themed/ThemedLogo';
import {
    Home,
    TestTube,
    ShoppingCart,
    Package,
    PackageOpen,
    Phone,
    Lock,
    Users,
    Settings,
    LogOut,
    ChevronDown,
    Menu,
    X,
} from 'lucide-react';
import userPortrait from '../../assets/sample-user-portrait.png';

const Header: React.FC = () => {
    const {user, logout} = useAuth();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const location = useLocation();
    const navigate = useNavigate();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isActive = (path: string) => location.pathname === path;

    const isAdmin = isUserAdmin(user?.token);

    return (
        <>
            <header className="bg-mld-menu-background shadow-sm">
                <div className="container mx-auto px-4 lg:px-6 py-4 flex justify-between items-center">
                    {/* Left side - Logo and desktop nav */}
                    <div className="flex items-center">
                        <Link to="/" className="mr-6 lg:mr-10">
                            <ThemedLogo className="h-5" />
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex">
                            <ul className="flex space-x-8">
                                <li>
                                    <Link
                                        to="/"
                                        className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                            isActive('/')
                                                ? 'text-mld-primary'
                                                : 'text-mld-menu-text hover:text-mld-primary'
                                        }`}>
                                        <Home className="h-4 w-4" />
                                        <span>Dashboard</span>
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/results"
                                        className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                            isActive('/results')
                                                ? 'text-mld-primary'
                                                : 'text-mld-menu-text hover:text-mld-primary'
                                        }`}>
                                        <TestTube className="h-4 w-4" />
                                        <span>Lab Results</span>
                                    </Link>
                                </li>
                                <li>
                                    <a
                                        href="https://mylabsdirect.com/at-home-test-kits/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-2 text-sm font-medium text-mld-menu-text hover:text-mld-primary transition-colors">
                                        <ShoppingCart className="h-4 w-4" />
                                        <span>Shop For Tests</span>
                                    </a>
                                </li>
                                <li>
                                    <Link
                                        to="/test-kits"
                                        className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                            isActive('/test-kits')
                                                ? 'text-mld-primary'
                                                : 'text-mld-menu-text hover:text-mld-primary'
                                        }`}>
                                        <PackageOpen className="h-4 w-4" />
                                        <span>Test Kits</span>
                                    </Link>
                                </li>
                                <li className="hidden">
                                    <Link
                                        to="/test-kits/activate"
                                        className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                            isActive('/test-kits/activate')
                                                ? 'text-mld-primary'
                                                : 'text-mld-menu-text hover:text-mld-primary'
                                        }`}>
                                        <Package className="h-4 w-4" />
                                        <span>Register a Kit</span>
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/support"
                                        className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                                            isActive('/support')
                                                ? 'text-mld-primary'
                                                : 'text-mld-menu-text hover:text-mld-primary'
                                        }`}>
                                        <Phone className="h-4 w-4" />
                                        <span>Support</span>
                                    </Link>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    {/* Right side - Mobile menu button + User menu */}
                    <div className="flex items-center space-x-2">
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden p-2 text-mld-menu-text hover:text-mld-primary transition-colors"
                            aria-label="Toggle mobile menu">
                            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>

                        {/* User Menu */}
                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                                className="flex items-center space-x-2 lg:space-x-3 text-mld-menu-text hover:text-mld-primary transition-colors">
                                <img
                                    src={userPortrait}
                                    alt="User Avatar"
                                    className="h-7 w-7 lg:h-8 lg:w-8 rounded-full"
                                />
                                <span className="hidden sm:block font-semibold text-sm">
                                    {(() => {
                                        const rawName =
                                            currentPatient?.firstName || user?.userInfo?.firstName || 'My Account';
                                        return rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();
                                    })()}
                                </span>
                                <ChevronDown
                                    className={`hidden sm:block h-4 w-4 transition-transform ${
                                        userMenuOpen ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>

                            {/* User Dropdown Menu */}
                            {userMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                    <Link
                                        to="/patients"
                                        className="flex items-center px-4 py-2 text-sm text-mld-menu-text hover:bg-gray-50 transition-colors"
                                        onClick={() => setUserMenuOpen(false)}>
                                        <Users className="h-4 w-4 mr-2" />
                                        Switch Patient
                                    </Link>
                                    <Link
                                        to="/settings"
                                        className="flex items-center px-4 py-2 text-sm text-mld-menu-text hover:bg-gray-50 transition-colors"
                                        onClick={() => setUserMenuOpen(false)}>
                                        <Settings className="h-4 w-4 mr-2" />
                                        Settings
                                    </Link>
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            className="flex items-center px-4 py-2 text-sm text-mld-menu-text hover:bg-gray-50 transition-colors"
                                            onClick={() => setUserMenuOpen(false)}>
                                            <Lock className="h-4 w-4 mr-2" />
                                            Admin Dashboard
                                        </Link>
                                    )}
                                    <hr className="my-2 border-gray-200" />
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors">
                                        <LogOut className="h-4 w-4 mr-2" />
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <div
                    className="lg:hidden bg-mld-menu-background border-t border-gray-200 shadow-lg"
                    ref={mobileMenuRef}>
                    <nav className="px-4 py-4">
                        <ul className="space-y-2">
                            <li>
                                <Link
                                    to="/"
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive('/')
                                            ? 'text-mld-primary bg-mld-primary/10'
                                            : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <Home className="h-5 w-5" />
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/results"
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive('/results')
                                            ? 'text-mld-primary bg-mld-primary/10'
                                            : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <TestTube className="h-5 w-5" />
                                    <span>Lab Results</span>
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="https://mylabsdirect.com/at-home-test-kits/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-mld-menu-text hover:text-mld-primary hover:bg-gray-50 transition-colors"
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <ShoppingCart className="h-5 w-5" />
                                    <span>Shop For Tests</span>
                                </a>
                            </li>
                            <li>
                                <Link
                                    to="/test-kits"
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive('/test-kits')
                                            ? 'text-mld-primary bg-mld-primary/10'
                                            : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <PackageOpen className="h-5 w-5" />
                                    <span>Test Kits</span>
                                </Link>
                            </li>
                            <li className="hidden">
                                <Link
                                    to="/test-kits/activate"
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive('/test-kits/activate')
                                            ? 'text-mld-primary bg-mld-primary/10'
                                            : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <Package className="h-5 w-5" />
                                    <span>Register a Kit</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/support"
                                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        isActive('/support')
                                            ? 'text-mld-primary bg-mld-primary/10'
                                            : 'text-mld-menu-text hover:text-mld-primary hover:bg-gray-50'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}>
                                    <Phone className="h-5 w-5" />
                                    <span>Support</span>
                                </Link>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </>
    );
};

export default Header;
